import React, { createContext, useState, useContext, useEffect } from 'react';
import { useWallet } from '@solana/wallet-adapter-react';
import { isWhitelisted, initializeWhitelist } from '../utils/whitelist';

// Create the whitelist context
const WhitelistContext = createContext();

/**
 * Custom hook to use the whitelist context
 */
export const useWhitelist = () => {
  const context = useContext(WhitelistContext);
  if (!context) {
    throw new Error('useWhitelist must be used within a WhitelistProvider');
  }
  return context;
};

/**
 * Whitelist provider component
 * Manages whitelist state and provides access to whitelist functionality
 */
export const WhitelistProvider = ({ children }) => {
  const { publicKey } = useWallet();
  const [isUserWhitelisted, setIsUserWhitelisted] = useState(false);
  const [isWhitelistLoaded, setIsWhitelistLoaded] = useState(false);
  const [whitelistError, setWhitelistError] = useState(null);

  // Load whitelist when component mounts
  useEffect(() => {
    const loadWhitelist = async () => {
      try {
        await initializeWhitelist();
        setIsWhitelistLoaded(true);
      } catch (error) {
        console.error('Failed to load whitelist:', error);
        setWhitelistError('Failed to load whitelist data');
      }
    };

    loadWhitelist();
  }, []);

  // Check if user is whitelisted when wallet changes
  useEffect(() => {
    if (publicKey) {
      const walletAddress = publicKey.toString();
      const whitelisted = isWhitelisted(walletAddress);
      setIsUserWhitelisted(whitelisted);
    } else {
      setIsUserWhitelisted(false);
    }
  }, [publicKey]);

  // Context value
  const value = {
    isUserWhitelisted,
    isWhitelistLoaded,
    whitelistError,
    checkWhitelist: (address) => isWhitelisted(address)
  };

  return (
    <WhitelistContext.Provider value={value}>
      {children}
    </WhitelistContext.Provider>
  );
};

export default WhitelistContext;
